import { useEffect, useState } from "react";
import TextInput from "../../../../Components/TextInput";
import {
  IDeltaHedgeAccountAggregateReport,
  IDeltaHedgeAccountChartsReport,
  IDeltaHedgeAccountChartsChartSearchParam,
} from "../../../../Interfaces/deltaHedgeAccounts";
import useSearchParam from "../../../../Hooks/useSearchParam";
import Button from "../../../../Components/Button";
import { packetErrorHandler } from "../../../../Utils/requestUtils";
import FullScreenLoading from "../../../../Components/Loading/FullScreenLoading";
import {
  getDeltaHedgeAccountsDropdown,
  getPriceChart,
  getTimeChart,
  getVolatilityChart,
} from "../../../../Requests/deltaHedgeAccounts";
import { LineChart } from "../../../../Components/Charts/LineChart";
import { RefreshOutlined } from "@mui/icons-material";
import Modal from "../../../../Components/Modal/Modal";
import DynamicDropdown from "../../../../Components/Dropdown/DynamicDropdown";
import { getGroups } from "../../../../Requests/optionDomain";

export enum ChartType {
  DailyLock = 1,
  Delta,
  Gama,
}

interface IChartsReports {
  volatility: IDeltaHedgeAccountChartsReport[];
  price: IDeltaHedgeAccountChartsReport[];
  time: IDeltaHedgeAccountChartsReport[];
}

interface IProps {
  row: IDeltaHedgeAccountAggregateReport;
}

const ChartUI = ({ row }: IProps) => {
  console.log("======row", row);
  const volatilitySearchParam = useSearchParam({
    initialSearchParam: {
      fromVolatility: 0.05,
      toVolatility: 0.5,
      pointCount: 50,
      ...JSON.parse(
        localStorage.getItem(
          "DeltaHedgeAccountAggregateVolatilityChartSearchParams"
        ) || "{}"
      ),
      groupId: row.groupId,
      sahraUserId: row.accountId,
      deltahedgeAccountId: row.deltahedgeAccountId,
    } as IDeltaHedgeAccountChartsChartSearchParam,
  });
  const priceSearchParam = useSearchParam({
    initialSearchParam: {
      fromVolatility: -0.2,
      toVolatility: 0.2,
      pointCount: 50,
      ...JSON.parse(
        localStorage.getItem(
          "DeltaHedgeAccountAggregatePriceChartSearchParams"
        ) || "{}"
      ),
      groupId: row.groupId,
      sahraUserId: row.accountId,
      deltahedgeAccountId: row.deltahedgeAccountId,
    } as IDeltaHedgeAccountChartsChartSearchParam,
  });

  const [chartReport, setChartReport] = useState({} as IChartsReports);
  const [loading, setLoading] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [isSearchParamOpen, setIsSearchParamOpen] = useState(false);

  useEffect(() => {
    if (
      checkDisabled(volatilitySearchParam.serverSearchParams) ||
      checkDisabled(priceSearchParam.serverSearchParams)
    )
      return setIsSearchParamOpen(true);
    setIsSearchParamOpen(false);
    fetchCharts();
  }, [
    volatilitySearchParam.serverSearchParams,
    priceSearchParam.serverSearchParams,
  ]);

  useEffect(() => {
    if (refreshFlag) {
      priceSearchParam.onSubmit();
      volatilitySearchParam.onSubmit();
    }
  }, [refreshFlag]);

  function fetchCharts() {
    setLoading(true);
    Promise.all([
      getVolatilityChart(volatilitySearchParam.serverSearchParams),
      getPriceChart(priceSearchParam.serverSearchParams),
      getTimeChart(priceSearchParam.serverSearchParams),
    ])
      .then((resp) => {
        setChartReport({
          price: resp[1].report,
          time: resp[2].report,
          volatility: resp[0].report,
        });
      })
      .catch((e) => {
        packetErrorHandler(e as any);
        setChartReport({
          price: [] as IDeltaHedgeAccountChartsReport[],
          volatility: [] as IDeltaHedgeAccountChartsReport[],
          time: [] as IDeltaHedgeAccountChartsReport[],
        });
      })
      .finally(() => {
        setLoading(false);
        setRefreshFlag(false);
      });
  }

  function checkDisabled(sp: IDeltaHedgeAccountChartsChartSearchParam) {
    if (!sp.fromVolatility || !sp.toVolatility || !sp.pointCount) return true;
    return false;
  }

  const getLabel = (chartType: ChartType) => {
    switch (chartType) {
      case ChartType.DailyLock:
        return "% بازده قفل روزانه";

      case ChartType.Delta:
        return "% دلتا";

      case ChartType.Delta:
        return "% گاما";

      default:
        return "";
    }
  };

  const getData = (
    report: IDeltaHedgeAccountChartsReport[],
    chartType: ChartType
  ) => {
    switch (chartType) {
      case ChartType.DailyLock:
        return report.map((r) => r.dailyLockedProfitPortfolio);

      case ChartType.Delta:
        return report.map((r) => r.deltaPercent);

      case ChartType.Gama:
        return report.map((r) => r.gamaPercent);

      default:
        return report.map((r) => r.dailyLockedProfitPortfolio);
    }
  };

  const submitHandler = () => {
    volatilitySearchParam.onSubmit();
    priceSearchParam.onSubmit();
    localStorage.setItem(
      "DeltaHedgeAccountAggregatePriceChartSearchParams",
      JSON.stringify(priceSearchParam.clientSearchParams)
    );
    localStorage.setItem(
      "DeltaHedgeAccountAggregateVolatilityChartSearchParams",
      JSON.stringify(volatilitySearchParam.clientSearchParams)
    );
  };

  const bothSearchParamsChangeHandler = (name: string, value: any) => {
    volatilitySearchParam.clientSearchParamChangeHandler(name, value);
    priceSearchParam.clientSearchParamChangeHandler(name, value);
  };

  const searchParamModalUI = (
    <div className="flex flex-col justify-start items-start gap-3 w-full mt-10">
      <div className="flex flex-row justify-start items-end gap-3 w-full">
        <div className="w-64">
          <TextInput
            label="شروع نوسان پذیری"
            name="fromVolatility"
            value={volatilitySearchParam.clientSearchParams.fromVolatility?.toString()}
            onChange={volatilitySearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="پایان نوسان پذیری"
            name="toVolatility"
            value={volatilitySearchParam.clientSearchParams.toVolatility?.toString()}
            onChange={volatilitySearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="تعداد نقاط"
            name="pointCount"
            value={volatilitySearchParam.clientSearchParams.pointCount?.toString()}
            onChange={volatilitySearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
      </div>
      <div className="flex flex-row justify-start items-end gap-3 w-full">
        <div className="w-64">
          <TextInput
            label="شروع قیمت"
            name="fromVolatility"
            value={priceSearchParam.clientSearchParams.fromVolatility?.toString()}
            onChange={priceSearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="پایان قیمت"
            name="toVolatility"
            value={priceSearchParam.clientSearchParams.toVolatility?.toString()}
            onChange={priceSearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
        <div className="w-64">
          <TextInput
            label="تعداد نقاط قیمت"
            name="pointCount"
            value={priceSearchParam.clientSearchParams.pointCount?.toString()}
            onChange={priceSearchParam.clientSearchParamChangeHandler}
            extraLabel
          />
        </div>
      </div>
      <div className="w-[150px] mr-auto">
        <Button
          onClick={submitHandler}
          text="محاسبه"
          variant="contained"
          disabled={checkDisabled(
            volatilitySearchParam.clientSearchParams &&
              priceSearchParam.clientSearchParams
          )}
        />
      </div>
    </div>
  );

  const getChartName = (chartName: "price" | "volatility" | "time") => {
    switch (chartName) {
      case "price":
        return "حساسیت به قیمت";

      case "volatility":
        return "حساسیت به نوسان پذیری";

      case "time":
        return "حساسیت به زمان";

      default:
        return "";
    }
  };

  const getCharts = (
    report: IDeltaHedgeAccountChartsReport[],
    chartName: "price" | "volatility" | "time"
  ) => (
    <div className="flex flex-col justify-start items-start w-1/3 h-full mt-2">
      <div className="text-xs text-center w-full -mb-5">
        {getChartName(chartName)}
      </div>
      <div className="h-1/3 w-full">
        <LineChart
          data={{
            labels: report.map((r) => r.point),
            datasets: [
              {
                label: getLabel(ChartType.DailyLock),
                backgroundColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                borderColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),

                pointRadius: report.map((r) => (r.isCurrent ? 3 : 2)),
                data: getData(report, ChartType.DailyLock),
              },
            ],
          }}
          maintainAspectRatio={false}
        />
      </div>
      <div className="h-1/3 w-full">
        <LineChart
          data={{
            labels: report.map((r) => r.point),
            datasets: [
              {
                label: getLabel(ChartType.Delta),
                backgroundColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                borderColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                pointRadius: report.map((r) => (r.isCurrent ? 3 : 2)),
                data: getData(report, ChartType.Delta),
              },
            ],
          }}
          maintainAspectRatio={false}
        />
      </div>
      <div className="h-1/3 w-full">
        <LineChart
          data={{
            labels: report.map((r) => r.point),
            datasets: [
              {
                label: getLabel(ChartType.Gama),
                backgroundColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                borderColor: report.map((r) =>
                  r.isCurrent ? "#dc2626" : "#519DE9"
                ),
                pointRadius: report.map((r) => (r.isCurrent ? 3 : 2)),
                data: getData(report, ChartType.Gama),
              },
            ],
          }}
          maintainAspectRatio={false}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col justify-start items-start relative h-full">
      {isSearchParamOpen ? (
        <Modal title="تنظیمات"
          UI={searchParamModalUI}
          onClose={() => setIsSearchParamOpen(false)}
        />
      ) : (
        <></>
      )}
      <div className="flex flex-row justify-start items-center absolute -top-8 left-0 gap-3">
        <div
          className="cursor-pointer"
          onClick={() =>
            checkDisabled(volatilitySearchParam.clientSearchParams) &&
            checkDisabled(priceSearchParam.clientSearchParams)
              ? () => {}
              : setRefreshFlag(true)
          }
        >
          <RefreshOutlined />
        </div>
        <DynamicDropdown
          dropdown={{ name: "groupId", placeholder: "گروه" }}
          getData={getGroups}
          onChange={bothSearchParamsChangeHandler}
          value={volatilitySearchParam.clientSearchParams.groupId?.toString()}
        />
        <DynamicDropdown
          dropdown={{ placeholder: "اکانت", name: "deltahedgeAccountId" }}
          onChange={bothSearchParamsChangeHandler}
          value={volatilitySearchParam.clientSearchParams.deltahedgeAccountId?.toString()}
          getData={getDeltaHedgeAccountsDropdown}
        />

        <div className="w-40">
          <Button
            text="تنظیمات"
            onClick={() => setIsSearchParamOpen(true)}
            variant="contained"
          />
        </div>
      </div>
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex flex-row justify-start items-start w-full h-full">
        {chartReport && chartReport.time && chartReport.time.length ? (
          getCharts(chartReport.time, "time")
        ) : (
          <></>
        )}
        {chartReport && chartReport.price && chartReport.price.length ? (
          getCharts(chartReport.price, "price")
        ) : (
          <></>
        )}
        {chartReport &&
        chartReport.volatility &&
        chartReport.volatility.length ? (
          getCharts(chartReport.volatility, "volatility")
        ) : (
          <></>
        )}
      </div>
      <div className="flex flex-col justify-around items-start h-full absolute -left-14">
        <div className="text-xs -rotate-90 text-start">
          درصد بازده قفل روزانه
        </div>
        <div className="text-xs -rotate-90 text-start pr-14">درصد دلتا</div>
        <div className="text-xs -rotate-90 text-start pr-14">درصد گاما</div>
      </div>
    </div>
  );
};

export default ChartUI;
